/**
 * Hooks TanStack Query — config livrables digitaux (S2.5e).
 *
 * Pattern aligné sur `useCatalog.ts`. Les mutations invalident la query
 * agrégée pour rafraîchir l'UI Settings après chaque action.
 *
 * Le polling de progression `regenerate_progress` est géré directement
 * dans le tab via `refetchInterval` conditionnel — pas besoin d'un hook
 * dédié.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { deliverableSettingsApi } from '@/utils/api/settings'
import type {
  DeliverableConfigUpdateRequest,
  DeliverableDocLang,
  DeliverableDocResponse,
  DeliverableRegenerateAllResponse,
  DeliverableSettingsResponse,
  ShopCarouselImageResponse,
  ShopCarouselScope,
} from '@/types/deliverable-settings'

export const deliverableSettingsKeys = {
  root: ['settings', 'deliverables'] as const,
}

interface ApiError {
  detail: string
  status?: number
}

/** Intervalle de polling actif quand un batch
 * `regenerate_deliverables` tourne (status `scheduled` ou `running`).
 * Sinon refetch seulement on mount/focus/mutation.
 */
const ACTIVE_BATCH_POLL_MS = 4_000

export function useDeliverableSettings() {
  return useQuery<DeliverableSettingsResponse>({
    queryKey: deliverableSettingsKeys.root,
    queryFn: () => deliverableSettingsApi.get(),
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return false
      const p = data.regenerate_progress
      const inProgress = (p.scheduled ?? 0) + (p.running ?? 0)
      return inProgress > 0 ? ACTIVE_BATCH_POLL_MS : false
    },
  })
}

export function useUpdateDeliverableConfig() {
  const qc = useQueryClient()
  return useMutation<DeliverableSettingsResponse, ApiError, DeliverableConfigUpdateRequest>({
    mutationFn: (payload) => deliverableSettingsApi.update(payload),
    onSuccess: (data) => {
      qc.setQueryData(deliverableSettingsKeys.root, data)
    },
  })
}

interface UploadDocVariables {
  lang: DeliverableDocLang
  file: File
}

export function useUploadDeliverableDoc() {
  const qc = useQueryClient()
  return useMutation<DeliverableDocResponse, ApiError, UploadDocVariables>({
    mutationFn: ({ lang, file }) => deliverableSettingsApi.uploadDocument(lang, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: deliverableSettingsKeys.root })
    },
  })
}

export function useDeleteDeliverableDoc() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, DeliverableDocLang>({
    mutationFn: (lang) => deliverableSettingsApi.deleteDocument(lang),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: deliverableSettingsKeys.root })
    },
  })
}

export function useRegenerateAllDeliverables() {
  const qc = useQueryClient()
  return useMutation<DeliverableRegenerateAllResponse, ApiError, void>({
    mutationFn: () => deliverableSettingsApi.regenerateAll(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: deliverableSettingsKeys.root })
    },
  })
}

interface AddShopCarouselImageVariables {
  file: File
  scope: ShopCarouselScope
}

export function useAddShopCarouselImage() {
  const qc = useQueryClient()
  return useMutation<ShopCarouselImageResponse, ApiError, AddShopCarouselImageVariables>({
    mutationFn: ({ file, scope }) => deliverableSettingsApi.addShopCarouselImage(file, scope),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: deliverableSettingsKeys.root })
    },
  })
}

interface UpdateShopCarouselImageScopeVariables {
  imageId: string
  scope: ShopCarouselScope
}

export function useUpdateShopCarouselImageScope() {
  const qc = useQueryClient()
  return useMutation<ShopCarouselImageResponse, ApiError, UpdateShopCarouselImageScopeVariables>({
    mutationFn: ({ imageId, scope }) =>
      deliverableSettingsApi.updateShopCarouselImageScope(imageId, scope),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: deliverableSettingsKeys.root })
    },
  })
}

export function useDeleteShopCarouselImage() {
  const qc = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: (imageId) => deliverableSettingsApi.deleteShopCarouselImage(imageId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: deliverableSettingsKeys.root })
    },
  })
}

export function useReorderShopCarouselImages() {
  const qc = useQueryClient()
  return useMutation<ShopCarouselImageResponse[], ApiError, string[]>({
    mutationFn: (orderedIds) => deliverableSettingsApi.reorderShopCarouselImages(orderedIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: deliverableSettingsKeys.root })
    },
  })
}
